import type { FourPillars, Pillar, PillarPosition } from '../types/pillars';
import type { FortuneRelation, YongsinEffect } from '../types/relations';
import type { YongSin } from '../types/strength';
import type { GyeokGukAnalysis } from '../types/gyeok-guk';
import type { Ohaeng } from '../types/elements';
import { getStemElement, getBranchElement } from '../core/five-elements';
import { stemIndex, branchIndex } from '../core/sexagenary';
import { COMBINES } from './heavenly-combine';
import { CLASH_PAIRS } from './heavenly-clash';
import { SIX_COMBINES, THREE_COMBINES, DIRECTIONAL_COMBINES } from './earthly-combine';
import { CLASHES } from './earthly-clash';
import { TRIPLE_PUNISH_1, TRIPLE_PUNISH_2, MUTUAL_PUNISH, SELF_PUNISH } from './earthly-punish';
import { DESTROY_PAIRS } from './earthly-destroy';
import { HARM_PAIRS } from './earthly-harm';

const ALL_POSITIONS: PillarPosition[] = ['year', 'month', 'day', 'hour'];
const NO_HOUR_POSITIONS: PillarPosition[] = ['year', 'month', 'day'];

/**
 * 운세 기둥과 원국 네 기둥 사이의 관계를 분석한다.
 *
 * 천간합/충, 육합/육충, 삼합/방합, 삼형/상형/자묘형/자형, 파, 해를 검출한다.
 */
export function analyzeFortuneRelations(
  fourPillars: FourPillars,
  fortunePillar: Pillar,
  noHour?: boolean,
  yongSin?: YongSin,
  gyeokGuk?: GyeokGukAnalysis,
): FortuneRelation[] {
  const POSITIONS = noHour ? NO_HOUR_POSITIONS : ALL_POSITIONS;
  const relations: FortuneRelation[] = [];

  const fortuneStemIdx = stemIndex(fortunePillar.stem);
  const fortuneBranchIdx = branchIndex(fortunePillar.branch);

  // Natal pillar data
  const natalStems = POSITIONS.map(pos => ({
    position: pos,
    idx: stemIndex(fourPillars[pos].stem),
    stem: fourPillars[pos].stem,
  }));

  const natalBranches = POSITIONS.map(pos => ({
    position: pos,
    idx: branchIndex(fourPillars[pos].branch),
    branch: fourPillars[pos].branch,
  }));

  // --- Pair relations (fortune 1 + natal 1) ---

  // 천간합
  for (const natal of natalStems) {
    for (const [s1, s2, result] of COMBINES) {
      if ((fortuneStemIdx === s1 && natal.idx === s2) || (fortuneStemIdx === s2 && natal.idx === s1)) {
        const [firstStem, secondStem] = fortuneStemIdx === s1
          ? [fortunePillar.stem, natal.stem] : [natal.stem, fortunePillar.stem];
        relations.push({
          type: '천간합',
          natalPillars: [natal.position],
          stems: [firstStem, secondStem],
          result,
          description: `${firstStem}${secondStem}합 → ${result}`,
        });
      }
    }
  }

  // 천간충
  for (const natal of natalStems) {
    for (const [s1, s2] of CLASH_PAIRS) {
      if ((fortuneStemIdx === s1 && natal.idx === s2) || (fortuneStemIdx === s2 && natal.idx === s1)) {
        const [firstStem, secondStem] = fortuneStemIdx === s1
          ? [fortunePillar.stem, natal.stem] : [natal.stem, fortunePillar.stem];
        relations.push({
          type: '천간충',
          natalPillars: [natal.position],
          stems: [firstStem, secondStem],
          description: `${firstStem}${secondStem}충`,
        });
      }
    }
  }

  // 육합
  for (const natal of natalBranches) {
    for (const [b1, b2, result] of SIX_COMBINES) {
      if ((fortuneBranchIdx === b1 && natal.idx === b2) || (fortuneBranchIdx === b2 && natal.idx === b1)) {
        const [firstBranch, secondBranch] = fortuneBranchIdx === b1
          ? [fortunePillar.branch, natal.branch] : [natal.branch, fortunePillar.branch];
        relations.push({
          type: '육합',
          natalPillars: [natal.position],
          branches: [firstBranch, secondBranch],
          ...(result ? { result } : {}),
          description: result
            ? `${firstBranch}${secondBranch}합 → ${result}`
            : `${firstBranch}${secondBranch}합 (변화없음)`,
        });
      }
    }
  }

  // 육충
  for (const natal of natalBranches) {
    for (const [b1, b2] of CLASHES) {
      if ((fortuneBranchIdx === b1 && natal.idx === b2) || (fortuneBranchIdx === b2 && natal.idx === b1)) {
        const [firstBranch, secondBranch] = fortuneBranchIdx === b1
          ? [fortunePillar.branch, natal.branch] : [natal.branch, fortunePillar.branch];
        relations.push({
          type: '육충',
          natalPillars: [natal.position],
          branches: [firstBranch, secondBranch],
          description: `${firstBranch}${secondBranch}충`,
        });
      }
    }
  }

  // 자묘형 (子↔卯)
  for (const natal of natalBranches) {
    if ((fortuneBranchIdx === MUTUAL_PUNISH[0] && natal.idx === MUTUAL_PUNISH[1]) ||
        (fortuneBranchIdx === MUTUAL_PUNISH[1] && natal.idx === MUTUAL_PUNISH[0])) {
      const [firstBranch, secondBranch] = fortuneBranchIdx === MUTUAL_PUNISH[0]
        ? [fortunePillar.branch, natal.branch] : [natal.branch, fortunePillar.branch];
      relations.push({
        type: '자묘형',
        natalPillars: [natal.position],
        branches: [firstBranch, secondBranch],
        description: `${firstBranch}${secondBranch}형 (무례지형)`,
      });
    }
  }

  // 자형 (Self punishment: fortune.branch === natal.branch && in SELF_PUNISH)
  if (SELF_PUNISH.includes(fortuneBranchIdx)) {
    for (const natal of natalBranches) {
      if (natal.idx === fortuneBranchIdx) {
        relations.push({
          type: '자형',
          natalPillars: [natal.position],
          branches: [fortunePillar.branch, natal.branch],
          description: `${fortunePillar.branch}${natal.branch} 자형`,
        });
      }
    }
  }

  // 파
  for (const natal of natalBranches) {
    for (const [b1, b2] of DESTROY_PAIRS) {
      if ((fortuneBranchIdx === b1 && natal.idx === b2) || (fortuneBranchIdx === b2 && natal.idx === b1)) {
        const [firstBranch, secondBranch] = fortuneBranchIdx === b1
          ? [fortunePillar.branch, natal.branch] : [natal.branch, fortunePillar.branch];
        relations.push({
          type: '파',
          natalPillars: [natal.position],
          branches: [firstBranch, secondBranch],
          description: `${firstBranch}${secondBranch}파`,
        });
      }
    }
  }

  // 해
  for (const natal of natalBranches) {
    for (const [b1, b2] of HARM_PAIRS) {
      if ((fortuneBranchIdx === b1 && natal.idx === b2) || (fortuneBranchIdx === b2 && natal.idx === b1)) {
        const [firstBranch, secondBranch] = fortuneBranchIdx === b1
          ? [fortunePillar.branch, natal.branch] : [natal.branch, fortunePillar.branch];
        relations.push({
          type: '해',
          natalPillars: [natal.position],
          branches: [firstBranch, secondBranch],
          description: `${firstBranch}${secondBranch}해`,
        });
      }
    }
  }

  // --- Triple relations (fortune 1 + natal 2) ---

  // 삼합: fortune + 2 natal branches form a THREE_COMBINES set
  for (const [b1, b2, b3, result] of THREE_COMBINES) {
    const tripleSet = [b1, b2, b3];
    if (!tripleSet.includes(fortuneBranchIdx)) continue;

    const needed = tripleSet.filter(b => b !== fortuneBranchIdx);

    if (needed.every(n => natalBranches.some(nb => nb.idx === n))) {
      const natalPositions = needed.map(n => natalBranches.find(nb => nb.idx === n)!.position);
      // 정식 순서: tripleSet [b1,b2,b3] 배열 순서를 따름
      const allBranches = tripleSet.map(idx =>
        idx === fortuneBranchIdx ? fortunePillar.branch : natalBranches.find(nb => nb.idx === idx)!.branch
      );
      relations.push({
        type: '삼합',
        natalPillars: natalPositions,
        branches: allBranches,
        result,
        description: `${allBranches.join('')} 삼합 → ${result}`,
      });
    }
  }

  // 방합: fortune + 2 natal branches form a DIRECTIONAL_COMBINES set
  for (const [b1, b2, b3, result] of DIRECTIONAL_COMBINES) {
    const tripleSet = [b1, b2, b3];
    if (!tripleSet.includes(fortuneBranchIdx)) continue;

    const needed = tripleSet.filter(b => b !== fortuneBranchIdx);
    if (needed.every(n => natalBranches.some(nb => nb.idx === n))) {
      const natalPositions = needed.map(n => natalBranches.find(nb => nb.idx === n)!.position);
      // 정식 순서: tripleSet [b1,b2,b3] 배열 순서를 따름
      const allBranches = tripleSet.map(idx =>
        idx === fortuneBranchIdx ? fortunePillar.branch : natalBranches.find(nb => nb.idx === idx)!.branch
      );
      relations.push({
        type: '방합',
        natalPillars: natalPositions,
        branches: allBranches,
        result,
        description: `${allBranches.join('')} 방합 → ${result}`,
      });
    }
  }

  // 삼형/상형: fortune + natal(s) in TRIPLE_PUNISH sets
  for (const tripleSet of [TRIPLE_PUNISH_1, TRIPLE_PUNISH_2]) {
    if (!tripleSet.includes(fortuneBranchIdx)) continue;

    const otherTwo = tripleSet.filter(b => b !== fortuneBranchIdx);
    const matchedNatals = natalBranches.filter(nb => otherTwo.includes(nb.idx));
    // Also count natal branches matching fortune's own index in the triple set
    const natalSameAsFortune = natalBranches.filter(nb => nb.idx === fortuneBranchIdx);
    const allMatched = [...matchedNatals, ...natalSameAsFortune];

    // Total members present: fortune(1) + matched natals (unique indices)
    const presentIndices = new Set([fortuneBranchIdx, ...allMatched.map(m => m.idx)]);
    const matchCount = presentIndices.size;

    if (matchCount >= 3) {
      // 삼형: all 3 present
      for (const natal of matchedNatals) {
        // 정식 순서: tripleSet 배열 내 순서를 따름
        const posF = tripleSet.indexOf(fortuneBranchIdx);
        const posN = tripleSet.indexOf(natal.idx);
        const [firstBranch, secondBranch] = posF < posN
          ? [fortunePillar.branch, natal.branch] : [natal.branch, fortunePillar.branch];
        relations.push({
          type: '삼형',
          natalPillars: [natal.position],
          branches: [firstBranch, secondBranch],
          description: `${firstBranch}${secondBranch}형`,
        });
      }
    } else if (matchCount === 2 && matchedNatals.length > 0) {
      // 상형: only 2 present, fortune is one of them
      for (const natal of matchedNatals) {
        const posF = tripleSet.indexOf(fortuneBranchIdx);
        const posN = tripleSet.indexOf(natal.idx);
        const [firstBranch, secondBranch] = posF < posN
          ? [fortunePillar.branch, natal.branch] : [natal.branch, fortunePillar.branch];
        relations.push({
          type: '상형',
          natalPillars: [natal.position],
          branches: [firstBranch, secondBranch],
          description: `${firstBranch}${secondBranch}형`,
        });
      }
    }
  }

  // 용신/격국 효과 태깅 (인자가 루프 내에서 불변이므로 한 번만 계산)
  if (yongSin || gyeokGuk) {
    const fortuneStemEl = getStemElement(fortunePillar.stem);
    const fortuneBranchEl = getBranchElement(fortunePillar.branch);
    const yEffect = yongSin ? getYongsinEffect([fortuneStemEl, fortuneBranchEl], yongSin) : undefined;
    const gEffect = gyeokGuk ? getGyeokgukEffect(fortuneStemEl, fortuneBranchEl, gyeokGuk) : undefined;

    for (const r of relations) {
      if (yEffect) r.yongsinEffect = yEffect;
      if (gEffect) r.gyeokgukEffect = gEffect;
    }
  }

  return relations;
}

/** 운의 오행이 용신/희신/기신/구신/한신 중 어디에 해당하는지 */
function getYongsinEffect(fortuneElements: Ohaeng[], yongSin: YongSin): YongsinEffect | undefined {
  for (const el of fortuneElements) {
    if (el === yongSin.yongSin) return '용신';
    if (el === yongSin.huiSin) return '희신';
    if (el === yongSin.giSin) return '기신';
    if (el === yongSin.guSin) return '구신';
    if (el === yongSin.hanSin) return '한신';
  }
  return undefined;
}

/** 운이 격국에 미치는 영향: 파격 사유를 강화하면 파격심화, 격을 지지하면 격국강화 */
function getGyeokgukEffect(
  fortuneStemEl: Ohaeng,
  fortuneBranchEl: Ohaeng,
  gyeokGuk: GyeokGukAnalysis,
): '격국강화' | '파격심화' | '중성' {
  if (gyeokGuk.status === '파격' && gyeokGuk.pagyeokReasons.length > 0) {
    // 파격 사유를 강화하는지 확인 (간략 판정)
    const formatYongSin = gyeokGuk.formatYongSin;
    if (formatYongSin) {
      const giEl = formatYongSin.yeokYong; // 역용이 있으면 그 오행이 기신 역할
      if (giEl && (fortuneStemEl === giEl || fortuneBranchEl === giEl)) {
        return '파격심화';
      }
    }
  }

  // 격국의 순용 오행이 운에서 오면 강화
  const sunYong = gyeokGuk.formatYongSin?.sunYong;
  if (sunYong && (fortuneStemEl === sunYong || fortuneBranchEl === sunYong)) {
    return '격국강화';
  }

  return '중성';
}
